import React, { useEffect, useState } from 'react';
import { useResponsive } from '@app/hooks/useResponsive';
import { Button, Col, Drawer, Form, message, Row, Image, Input, Tag } from 'antd';
import { getProductForOrdersApi } from '@app/api/product.api';
import { ProductModel } from '@app/interfaces/product';
import useDebounce from '@app/hooks/useDebounce';
import { Card } from '@app/components/common/BaseCard/BaseCard.styles';
import { PlusCircleFilled, SearchOutlined } from '@ant-design/icons';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { addProductItem } from '@app/store/slices/orderSlice';
import { ProductItemsProp } from '@app/interfaces/order';
import { formatMoney } from '@app/utils/utils';
import ProductType from '@app/components/inventory/product/components/ProductType';
import { MdLightbulbOutline, MdOutlineInventory2 } from 'react-icons/md';

interface ProductItemListProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
}

const ProductItemList: React.FC<ProductItemListProps> = ({ open, setOpen, title }) => {
  const { mobileOnly, isTablet, isDesktop } = useResponsive();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [products, setProducts] = useState<ProductModel[]>([]);

  const dispatch = useAppDispatch();
  const debouncedSearchTerm = useDebounce(searchTerm, 100);

  useEffect(() => {
    fetchProducts();
  }, [debouncedSearchTerm]);

  const fetchProducts = async () => {
    try {
      const newParams = {
        q: debouncedSearchTerm,
        page: 1,
        per_page: 20,
      };

      const response = await getProductForOrdersApi(newParams);
      const data = response?.rows.map((item) => item.attributes) || [];

      setProducts(data);
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const onClose = () => {
    setOpen(false);
  };

  const deleteProductOutOfStock = (id: number) => {
    setProducts((preProducts) => preProducts.filter((product) => Number(product.id) !== id));
  };

  const handleAddItem = (item: ProductModel) => {
    const newItem: ProductItemsProp = {
      id: item.id,
      name: item.name,
      unit: item.unit,
      qty: 1,
      price: item.selling_price,
      selling_price: item.selling_price,
      amount: item.selling_price,
      product_code: item.product_code,
      product_type: item.product_type || '',
      onhand: item.onhand,
      remark: '',
    };

    dispatch(addProductItem(newItem));

    if (item.onhand === 1) {
      deleteProductOutOfStock(Number(item.id));
    } else {
      const updateData: ProductModel = {
        ...item,
        onhand: item.onhand - 1,
      };
      //setProducts((prevProducts) => prevProducts.map(product => product.id === newData.id ? newData : product))
      //dispatch(updateProduct({productId: Number(item.id), formData: updateData}))
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <Drawer
      title={
        <div className="tw-text-on-surface" style={{
          fontSize: '16px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <SearchOutlined />
          {title}
        </div>
      }
      width={mobileOnly ? '100%' : isTablet && !isDesktop ? '90%' : 800}
      onClose={onClose}
      open={open}
      bodyStyle={{
        paddingBottom: 80,
        background: '#f5f7fa',
        padding: '16px'
      }}
      extra={
        <Button
          onClick={onClose}
          style={{
            borderRadius: '6px',
            fontWeight: 500
          }}
        >
          Đóng
        </Button>
      }
    >
      <div style={{
        marginBottom: '16px',
        padding: '12px 16px',
        background: 'rgba(0, 76, 76, 0.06)',
        borderRadius: '8px',
        border: '1px solid rgba(0, 76, 76, 0.2)',
        fontSize: '13px',
        color: 'var(--primary-color)',
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        <MdLightbulbOutline style={{ fontSize: 16 }} />
        Chọn sản phẩm để thêm vào đơn hàng
      </div>

      <Form layout="vertical">
        <Row>
          <Col span={24}>
            <Form.Item style={{ marginBottom: '16px' }}>
              <Input
                placeholder="Tìm kiếm theo tên, mã sản phẩm..."
                prefix={<SearchOutlined className="tw-text-outline" />}
                onChange={handleSearchChange}
                allowClear
                size="large"
                style={{
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Row gutter={[12, 12]}>
        {products && products.length > 0 ? (
          products.map((item, index) => (
            <Col xs={12} sm={8} md={6} key={index}>
              <Card
                $autoHeight={true}
                $padding={12}
                style={{
                  borderRadius: '10px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative'
                }}
                className="product-item-card tw-border tw-border-solid tw-border-surface-container-low tw-bg-surface-container-lowest"
                onMouseEnter={(e: any) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 76, 76,0.2)';
                  e.currentTarget.style.borderColor = 'var(--primary-color)';
                }}
                onMouseLeave={(e: any) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                  e.currentTarget.style.borderColor = '';
                }}
              >
                <div style={{
                  textAlign: 'center',
                  marginBottom: '8px'
                }}>
                  <Image
                    width={80}
                    height={80}
                    src={item?.image_url ?? undefined}
                    style={{
                      borderRadius: '8px',
                      objectFit: 'cover'
                    }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
                  />
                </div>

                <div className="tw-text-on-surface" style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  marginBottom: '4px',
                  textAlign: 'center',
                  lineHeight: '1.4',
                  minHeight: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {item.name}
                </div>

                <div style={{
                  textAlign: 'center',
                  marginBottom: '6px'
                }}>
                  <Tag
                    color="blue"
                    style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '4px'
                    }}
                  >
                    #{item.product_code}
                  </Tag>
                </div>

                <div className="tw-bg-surface-subtle" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 8px',
                  borderRadius: '6px',
                  marginBottom: '6px',
                  fontSize: '12px'
                }}>
                  <span className="tw-text-on-surface-variant">Giá bán:</span>
                  <span className="tw-text-[13px] tw-font-semibold tw-text-primary">
                    {formatMoney(Number(item.selling_price))}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 8px',
                  background: item.onhand > 0 ? '#f6ffed' : '#fff1f0',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  fontSize: '12px'
                }}>
                  <span className="tw-text-on-surface-variant">Tồn kho:</span>
                  <span className={`tw-text-[13px] tw-font-semibold ${item.onhand > 0 ? 'tw-text-success-teal' : 'tw-text-error'}`}>
                    {Number(item.onhand)} {item.unit}
                  </span>
                </div>

                <div style={{
                  marginBottom: '8px',
                  textAlign: 'center'
                }}>
                  <ProductType name={item.product_type} />
                </div>

                <Button
                  type="primary"
                  icon={<PlusCircleFilled />}
                  onClick={() => handleAddItem(item)}
                  block
                  style={{
                    borderRadius: '6px',
                    fontWeight: 500,
                    height: '32px',
                    fontSize: '13px'
                  }}
                  disabled={item.onhand <= 0}
                >
                  Thêm vào đơn
                </Button>
              </Card>
            </Col>
          ))
        ) : (
          <Col span={24}>
            <div className="tw-rounded-[10px] tw-border tw-border-dashed tw-border-outline-variant tw-bg-surface-container-lowest" style={{
              textAlign: 'center',
              padding: '60px 20px'
            }}>
              <MdOutlineInventory2 style={{
                fontSize: '48px',
                marginBottom: '16px',
                opacity: 0.3
              }} />
              <div className="tw-text-on-surface-variant" style={{
                fontSize: '15px',
                fontWeight: 500
              }}>
                Không tìm thấy sản phẩm
              </div>
              <div className="tw-text-outline" style={{
                fontSize: '13px',
                marginTop: '8px'
              }}>
                Thử tìm kiếm với từ khóa khác
              </div>
            </div>
          </Col>
        )}
      </Row>
    </Drawer>
  );
};

export default ProductItemList;
